import type { MaybeRefOrGetter } from 'vue';
import type { M2MStructure } from '#layers/data-model/public';
import type { RelationPermissionsReturn } from '#layers/forms/types/permissions';
import { computed, toValue } from 'vue';
import { useRelationPermissionsBase } from './use-relation-permissions-base';

export interface UseM2MPermissionsOptions {
	m2mStructure: MaybeRefOrGetter<M2MStructure | null>;
}

export function useM2MPermissions(options: UseM2MPermissionsOptions): RelationPermissionsReturn {
	const structure = computed(() => toValue(options.m2mStructure));

	// M2M: isCreateForm is irrelevant -- junction rows are always created/deleted,
	// never updated, regardless of whether the parent form is in create or update mode.

	// Call 1: backward M2M field (source.field) -> junction collection + source-side FKs
	const {
		permissionsStore,
		oppositeColId: junctionColId,
		oppositeConstrainedFieldIds: junctionSourceFkIds,
	} = useRelationPermissionsBase({
		field: () => structure.value?.source.field ?? null,
		isCreateForm: false,
	});

	// Call 2: junction.toTarget -> target collection + target-side FKs
	const {
		oppositeColId: targetColId,
		localConstrainedFieldIds: junctionTargetFkIds,
	} = useRelationPermissionsBase({
		field: () => structure.value?.junction.toTarget ?? null,
		isCreateForm: false,
	});

	// Combined junction FK IDs (both source + target sides)
	const allJunctionFkIds = computed(() => [
		...junctionSourceFkIds.value,
		...junctionTargetFkIds.value,
	]);

	return {
		canConnect: computed(() => {
			if (!structure.value || !junctionColId.value || !targetColId.value) return false;
			if (allJunctionFkIds.value.length === 0) return false;
			return permissionsStore.canRead(targetColId.value)
				&& permissionsStore.canCreate(junctionColId.value)
				&& permissionsStore.canCreateFields(junctionColId.value, allJunctionFkIds.value);
		}),
		canCreate: computed(() => {
			if (!structure.value || !junctionColId.value || !targetColId.value) return false;
			if (allJunctionFkIds.value.length === 0) return false;
			return permissionsStore.canCreate(targetColId.value)
				&& permissionsStore.canCreate(junctionColId.value)
				&& permissionsStore.canCreateFields(junctionColId.value, allJunctionFkIds.value);
		}),
		canEdit: computed(() => {
			if (!structure.value || !targetColId.value) return false;
			return permissionsStore.canUpdate(targetColId.value);
		}),
		canDisconnect: computed(() => {
			if (!structure.value || !junctionColId.value) return false;
			return permissionsStore.canDelete(junctionColId.value);
		}),
	};
}
